import {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import Environment from '../../../../../environment';
import useArenaStore from '@/Store/ArenaStore';

// ── useGetArenaGames ─────────────────────────────────────────────────────────
// Ongoing arena boards from the game service's REST surface:
//   GET {GAME_SERVICE_URL}/arenaGames?arenaId=&page=&pageSize=&userId=
//   → {code: 200, games: [{tableId, gameSeq, whiteId, blackId, whiteUsername,
//      blackUsername, whiteRating, blackRating, whiteBerserk, blackBerserk}],
//      page, pageSize, total, totalPages, myGame: {...game, inPage} | null}
//   (ids as strings, berserk booleans; only tables currently in progress).
//
// Paged because a big arena has hundreds of boards at once. Server-side the
// order is now stable (ccm_arena_pairings.game_seq) — it used to come from a
// redis hash whose iteration order was arbitrary, so page 2 could repeat
// page 1. `myGame` is the caller's own board, pinned regardless of page.
//
// Auth mirrors the game service's other REST routes (cc-conn-mgr gsApiCheck):
// an `x-api-key` header carrying Environment.X_API_KEY. GAME_SERVICE_URL is
// the socket origin (ws(s):// in prod) — REST rides the same host over
// http(s), hence the scheme swap.
// Polls every 10s ONLY while the arena is running (interval cleared, list
// emptied otherwise — the boards drain once the arena stops pairing).

export interface ArenaOngoingGame {
  tableId: string;
  gameSeq?: number;
  whiteId: string;
  blackId: string;
  whiteUsername: string;
  blackUsername: string;
  whiteRating: number;
  blackRating: number;
  whiteBerserk: boolean;
  blackBerserk: boolean;
}

export interface ArenaMyGame extends ArenaOngoingGame {
  inPage: boolean;
}

const GAME_SERVICE_REST_BASE = String(
  Environment.GAME_SERVICE_URL || '',
).replace(/^ws/, 'http');

export default function useGetArenaGames(
  arenaId?: string | number,
  page: number = 1,
  pageSize: number = 10,
  userId?: string | number,
) {
  const arenaState = useArenaStore(state => state.status?.state);
  const [games, setGames] = useState<ArenaOngoingGame[]>([]);
  const [myGame, setMyGame] = useState<ArenaMyGame | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const refetch = useCallback(async () => {
    if (!arenaId || !GAME_SERVICE_REST_BASE) {
      return;
    }
    try {
      const response = await axios.get(`${GAME_SERVICE_REST_BASE}/arenaGames`, {
        params: {
          arenaId: String(arenaId),
          page: String(page),
          pageSize: String(pageSize),
          ...(userId ? {userId: String(userId)} : {}),
        },
        headers: {'x-api-key': Environment.X_API_KEY},
      });
      if (response?.data?.code === 200 && Array.isArray(response.data.games)) {
        setGames(response.data.games);
        setMyGame(response.data.myGame ?? null);
        setTotal(Number(response.data.total) || 0);
        setTotalPages(Math.max(1, Number(response.data.totalPages) || 1));
      }
    } catch (e: any) {
      // best-effort polling — keep the previous list on a failed tick
      console.warn('arenaGames fetch failed:', e?.message);
    }
  }, [arenaId, page, pageSize, userId]);

  useEffect(() => {
    // Keep polling through 'finishing' — the buzzer stops NEW pairings but
    // in-flight games drain for a while and must stay listed (the tab count
    // from standings broadcasts keeps updating during the drain).
    if (arenaState !== 'running' && arenaState !== 'finishing') {
      setGames([]);
      setMyGame(null);
      setTotal(0);
      setTotalPages(1);
      return;
    }
    refetch();
    const intervalId = setInterval(refetch, 10000);
    return () => clearInterval(intervalId);
  }, [arenaState, refetch]);

  return {games, myGame, total, totalPages, refetch};
}
